import Button from "@/common/components/Button";
import { barChartLabelFormatter } from "@/common/utils/barChartLabelFormatter";
import { cn } from "@/common/utils/cn";
import { DashboardComponentData } from "@/features/company/types/dashboards/dashboardComponent.type";
import { ChevronRightIcon } from "lucide-react";

interface TableRowProps {
  uniqueId: string;
  index: number;
  row: any;
  primarySettings: DashboardComponentData['data']['primary_settings'];
  seriesSettings: DashboardComponentData['data']['series_settings'];
  isExpanded: boolean;
  hasChildren: boolean;
  onToggle: () => void;
}

const rowLevelOpacityClass: Record<number, string> = {
  0: 'text-neutral-50 font-semibold',
  1: 'text-neutral-50/95 font-medium bg-white-100',
  2: 'text-neutral-50/90 font-normal bg-white-100/10',
  3: 'text-neutral-50/85 bg-white-100/15',
};

const TableRow = ({
  uniqueId,
  index,
  row,
  primarySettings,
  seriesSettings,
  isExpanded,
  hasChildren,
  onToggle,
}: TableRowProps) => {
  const rowLevel: number = row.meta_attributes?.row_stack?.filter((value: any, index: number) => value && index)?.length || 0;
  const isTotalRow = row.meta_attributes?.is_total_row;

  return (
    <tr>
      <td className={cn(
        'border border-l-0 border-black-300 text-left text-xs whitespace-pre-wrap p-2',
        rowLevelOpacityClass[Math.min(rowLevel, 3)],
        isTotalRow && 'bg-white-100 font-bold',
      )}>
        <div className={cn('flex items-center gap-1', hasChildren && 'cursor-pointer transition-colors rounded p-1 -m-1 hover:bg-white-100')} onClick={onToggle}>
          <span>
            {new Array(rowLevel)?.fill(null)?.map(() => `\t\t`).join('')}
          </span>

          {hasChildren && (
            <Button variant="link-white">
              <ChevronRightIcon
                strokeWidth={1.5}
                size={16}
                className={cn('text-inherit transition-transform', isExpanded && 'rotate-90')}
              />
            </Button>
          )}

          <span>
            {row[primarySettings.query_identifier]}
          </span>
        </div>
      </td>

      {seriesSettings && Object.entries(seriesSettings)?.map(([key, value]) => (
        <td key={`${uniqueId}.${index}.${key}`} className={cn(
          'border border-r-0 border-black-300 text-left text-xs whitespace-pre-wrap p-2',
          rowLevelOpacityClass[Math.min(rowLevel, 5)],
          isTotalRow && 'bg-white-100 font-bold',
        )}>
          {barChartLabelFormatter(
            row[key] as string,
            value.format_parser,
            '--',
          )}
        </td>
      ))}
    </tr>
  );
}

export default TableRow;
